import * as THREE from 'three'
import { useEffect, useRef, useMemo, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { Float, Html, useGLTF, useHelper, Outlines } from '@react-three/drei'
import { useControls, button } from 'leva'
import { getCurrentScrollPercent, getComplementaryColor, isMobile } from './Utils'
import { bio, experiences, preload_assets } from './data/PortfolioData'
import { colorPalettes } from './data/ColorPalettes'
import { TopCirclePaddles } from './components/TopCirclePaddles'
import Clouds from './components/Clouds'
import PortfolioVisualComponent from './components/PortfolioVisualComponent'
import { updateDebugHighlightExperienceDistanceToCenterBall, updatePorfolioVisualComponentCacharroHeaderText } from './components/PortfolioVisualComponent'

export default function Experience({
    scrollController,
}) {
    // console.log("recreating Experience! calling return")

    const { scene, camera } = useThree()

    let {
        showNumbers: showDebugNumbers,
        showLines: showDebugLines,
        showCenterSphere: showDebugCenterSphere,
    } = useControls('debugToggles', {
        showNumbers: false,
        showLines: false,
        showCenterSphere: false,
    })
    if (showDebugLines) {
        showDebugCenterSphere = true
    }

    const [controlColors, setControlColors] = useControls('colors', () => ({
        colorBackground: {
            value: '#6482ad'
        },
        colorText: {
            value: '#241a1a'
        },
        colorCacharro: {
            value: '#fff6da'
        },
        colorPaddles: {
            value: '#fff6da'
        },
        colorPaddleHighlight: {
            value: '#93cb52'
        },
        colorRamp: {
            value: '#D8C4B6'
        },
        colorTube: {
            value: '#a94a4a'
        },
        'random-palette': button(() => {
            const newPalette = colorPalettes[Math.floor(Math.random() * colorPalettes.length)]['colors']
            updatePalette(newPalette)
        }),
        'random-colors': button(() => {
            updatePalette(currentPalette)
        }),
    }))
    let currentPalette = [
        controlColors.colorBackground,
        controlColors.colorText,
        controlColors.colorCacharro,
        controlColors.colorRamp,
        controlColors.colorPaddles,
        controlColors.colorPaddleHighlight,
        controlColors.colorTube
    ]

    const updateBackgroundColors = () => {
        const html = document.querySelector('html')
        const body = document.querySelector('body')
        html.style.backgroundColor = controlColors.colorBackground
        body.style.backgroundColor = controlColors.colorBackground
    }

    const updateCSSColors = () => {
        updateBackgroundColors()

        document.body.style.color = controlColors.colorText

        const roleAnimatedText = document.querySelector('.role-animated-text')
        roleAnimatedText.style.background = controlColors.colorTube

        const roleLines = document.querySelectorAll('.role-line')
        roleLines.forEach(line => {
            line.style.color = controlColors.colorRamp
        })

        const titleHeaders = document.querySelectorAll('.title-header')
        titleHeaders.forEach(titleHeader => {
            titleHeader.style.textShadow = `0px 0px 6px ${controlColors.colorRamp}`
            titleHeader.style.webkitTextStroke = `1.0px ${controlColors.colorRamp}`
        })

        const experienceInfoContainer = document.querySelector('.experience-info-container')
        if (experienceInfoContainer == undefined) {
            // this happens some times and not sure why.. but still works
            console.error("!! B) Experience.updateCSSColors: experienceInfoContainer not found")
            return
        }
        experienceInfoContainer.style.textShadow = `-0.3px -0.3px 0 ${controlColors.colorTube}, 0.3px -0.3px 0 ${controlColors.colorTube}, -0.3px 0.3px 0 ${controlColors.colorTube}, 0.3px 0.3px 0 ${controlColors.colorTube}`
    }
    updateBackgroundColors()

    // update css colors when controlColors changed
    useEffect(() => {
        updateCSSColors()
    }, [controlColors])

    const updatePalette = (newPalette, shuffle = true) => {
        // console.log("newPalette color 0: " + newPalette[0])
        const shuffledPalette = shuffle ? newPalette.sort(() => Math.random() - 0.5) : newPalette
        controlColors.colorBackground = shuffledPalette[0]
        controlColors.colorText = getComplementaryColor(shuffledPalette[0])
        controlColors.colorCacharro = shuffledPalette[1]
        controlColors.colorRamp = shuffledPalette[2]
        controlColors.colorTube = shuffledPalette[3]

        updateCSSColors()

        setControlColors(controlColors)
        currentPalette = shuffledPalette
    }

    const { useNormalMaterial } = useControls('materials', {
        wireframe: {
            value: false,
            onChange: (value) => {
                normalMaterial.wireframe = value
                regularMaterialBall.wireframe = value
                regularMaterialCircleRamp.wireframe = value
                regularMaterialTube.wireframe = value
            }
        },
        useNormalMaterial: {
            value: false
        }
        // position:
        // {
        //     value: { x: -1, y: 0 },
        //     step: 0.01,
        //     joystick: 'invertY',
        //     onChange: (value) => {
        //         // meshRef.current.position.set(value.x, value.y, 0)
        //     }
        // },
        // color: {
        //     value: '#00ffe2',
        //     onChange: (value) => {
        //         material2.color = new THREE.Color(value)
        //     }
        // }
    })

    const [
        cameraLerpFactor,

        totalCameraScrollCameraUnits,
        bioSectionCameraUnitsHeight,
        contactSectionCameraUnitsHeight,
        oneExperienceCameraUnitsHeight,

        topCircleOffsetY,
        centerTubeOffsetY,
        centerTubeScaleY,
        startExperiencesCameraUnitsOffset,
        experiencesContainerOffsetY,
        experiencesBallRampOffsetZ,
        ballOverRampOffsetZ,
        contactSectionOffsetY,

        initialLetBallFixedInsideRingCameraUnits,
        initialFallingBallCameraUnits,
        finalBallOffsetY,

        startExperiencesRotationCameraUnits,
        endExperiencesRotationCameraUnits,
        totalExperiencesRotationCameraUnitsHeight,
        finalStopRotatingBallCameraUnits,

        startContactSectionCameraUnits,
    ] = scrollController

    const cameraGroup = new THREE.Group()
    const models3dRef = useRef()
    const centerTubeRef = useRef()
    const rampsBallContainerRef = useRef()
    const maxCloudDistanceWidth = 8.5

    const experiencesContainerRef = useRef()
    const experienceInfoContainerRef = useRef()
    // console.log("experiencesContainerRef")
    // console.log(experiencesContainerRef)

    // Add target position for smooth camera movement
    const targetScrollPercent = useRef(-1)
    const currentScrollPercent = useRef(-1)
    const disableScrollLerpOne = useRef(false)

    const totalClouds = 4
    const cloudSeparationY = 3.0

    const totalPaddles = 32
    const topCirclePaddlesRef = useRef()
    const [ignorePaddlesMouseReaction, setIgnorePaddlesMouseReaction] = useState(false)

    const experiencesRef = []

    const experiencesByRamp = 2.0
    const experiencesSeparationCount = 12.0

    // TODO: está hardcodeado para que funcione separando 12 veces y que haya 13 experiencias.. me gustaría que fuera con un número más arbitrario pero no me da el coconut. Pero ahora sí funciona para múltiplos de 4 + 1 (1, 5, 9, 13, 17..). Para múltimos de 2 + 1 podría tb haciendo que la rampa sea de la mitad de tamaño y añadiendo el doble para que quede bien el último tramo.
    const experiencesRotationSeparation = Math.PI * 2 / experiencesSeparationCount
    const rampBallScaleY = oneExperienceCameraUnitsHeight * experiencesByRamp * 2.0
    const totalRampBalls = Math.ceil(experiences.length / experiencesByRamp) + 1.0
    const experiencesContainerTotalRotations = Math.PI * 2.0 * ((experiences.length - 1) / experiencesSeparationCount)
    var rampSpeed = Math.PI * 2 * (experiences.length - 1) * oneExperienceCameraUnitsHeight / rampBallScaleY
    // var rampSpeed = 12.0 // así se mantiene constante la caída (bueno, más o menos) (bueno, parece que se ha solucionado solo..)
    // experiences.length - 1
    // var rampSpeed = Math.PI * 2 * (experiences.length / experiencesByRamp)// * oneExperienceCameraUnitsHeight // / rampBall.scale.y
    // var rampSpeed = (experiences.length - 1) * oneExperienceCameraUnitsHeight / rampBallScaleY
    // rampsBallContainerRef.current.rotation.y = experiencesRotationPercentage * Math.PI * 2 * rampSpeed

    // console.log("totalCameraScrollCameraUnits " + totalCameraScrollCameraUnits)
    // console.log("bioSectionCameraUnitsHeight " + bioSectionCameraUnitsHeight)
    // console.log("contactSectionCameraUnitsHeight " + contactSectionCameraUnitsHeight)
    // console.log("oneExperienceCameraUnitsHeight " + oneExperienceCameraUnitsHeight)
    // console.log("startExperiencesRotationCameraUnits " + startExperiencesRotationCameraUnits)
    // console.log("endExperiencesRotationCameraUnits " + endExperiencesRotationCameraUnits)
    // console.log("finalStopRotatingBallCameraUnits " + finalStopRotatingBallCameraUnits)

    const debugLimitLines = [
        initialLetBallFixedInsideRingCameraUnits,
        initialFallingBallCameraUnits,
        startExperiencesRotationCameraUnits,
        endExperiencesRotationCameraUnits,
        finalStopRotatingBallCameraUnits,
    ]

    // console.log("Experience.debuglines: minCameraUnitsToStartFallingBallScroll " + minCameraUnitsToStartFallingBallScroll)
    // console.log("Experience.debuglines: initialFallingBallCameraUnits " + initialFallingBallCameraUnits)
    // console.log("Experience.debuglines: startExperiencesRotationOffset " + startExperiencesRotationOffset)
    // console.log("Experience.debuglines: experiencesReachedLastRotationCameraUnitsOffset " + experiencesReachedLastRotationCameraUnitsOffset)

    const portfolioAssetsGLTF = useGLTF(preload_assets.glbUrls.portfolio_assets)
    const cloudGLTF = useGLTF(preload_assets.glbUrls.cloud)
    const envelopeGLTF = useGLTF(preload_assets.glbUrls.envelope)
    // console.log(envelopeGLTF)
    // console.log(envelopeGLTF.materials)

    const [cloudPositions, setCloudPositions] = useState([])
    const [cloudRotations, setCloudRotations] = useState([])
    useEffect(() => {
        const randomPositions = []
        const randomRotations = []
        const possibleInitialMinPositionX = -maxCloudDistanceWidth * 0.9
        const possibleInitialMaxPositionX = maxCloudDistanceWidth * 0.9
        const minDiff = maxCloudDistanceWidth * 0.3
        // const initialPositionX = -maxCloudDistanceWidth * 0.5 + Math.random() * maxCloudDistanceWidth * 0.5
        let lastRandomPositionX = 0.0
        for (let i = 0; i < totalClouds; i++) {
            // randomPositions.push(Math.random() * initialPositionX + (maxCloudDistanceWidth * Math.random() - maxCloudDistanceWidth * 0.5))
            let nextRandomPositionX = (possibleInitialMaxPositionX - possibleInitialMinPositionX) * Math.random() + possibleInitialMinPositionX
            if (Math.abs(nextRandomPositionX - lastRandomPositionX) < minDiff) {
                nextRandomPositionX = lastRandomPositionX + minDiff
            }
            randomPositions.push(nextRandomPositionX)
            randomRotations.push(Math.random() * Math.PI * 2.0)
            lastRandomPositionX = nextRandomPositionX
        }
        setCloudPositions(randomPositions)
        setCloudRotations(randomRotations)
    }, [])

    let envelopePosition = new THREE.Vector3(
        1.9,
        - (startContactSectionCameraUnits + contactSectionCameraUnitsHeight / 2.0) - 0.4,
        0.0
    )
    if (isMobile()) {
        envelopePosition.set(
            0.0,
            - (startContactSectionCameraUnits + contactSectionCameraUnitsHeight / 2.0) - 1.6,
            0.0
        )
    }

    const movingBallInitialRotation = new THREE.Vector3(
        0.0,
        0.0,
        Math.PI / 1.0
    )

    useEffect(() => {
        // allow to change control magic numbers live, if the scroll lerp is enabled makes an effect that everything appears from bottom
        currentScrollPercent.current = -1
        disableScrollLerpOne.current = true
    }, [scrollController])

    const [
        debugCenterObject,
        cloud,
        movingBallPivot, movingBall, centerTube, rampBall, cacharroHolder,
        envelope, envelopeAnimationAction, envelopeAnimationDuration,
        cartaAnimationAction, tapa,
        titleNameNormal, titleNameWithDog,
        canvasContainer, defaultCanvasContainerZIndex,
        overlayContainer, defaultOverlayContainerZIndex,
        portfolioSectionsContainer, defaultPortfolioSectionsContainerZIndex,
    ] = useMemo(() => {
        console.log("portfolioAssets changed! calling useMemo")

        // add a ball in front of camera to see the center of the scene
        const geometry = new THREE.SphereGeometry(0.05, 8, 8)
        const debugCenterObject = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
            color: 0xff0000,
            // side: THREE.DoubleSide,
            wireframe: true
        }))
        debugCenterObject.position.z = -2

        // find javascript class dom element
        const titleNameNormal = document.querySelector('.title-header.my-name-normal')
        const titleNameWithDog = document.querySelector('.title-header.my-name-with-dog')

        const portfolioSectionsContainer = document.getElementById("portfolio-sections-container")
        const canvasContainer = document.getElementById("canvas-container")
        const overlayContainer = document.getElementById("overlay-container")
        const computedStylePortfolioSectionsContainerZIndex = getComputedStyle(portfolioSectionsContainer).zIndex
        const computedStyleCanvasContainerZIndex = getComputedStyle(canvasContainer).zIndex
        const computedStyleOverlayContainerZIndex = getComputedStyle(overlayContainer).zIndex
        // this values can be -1 if the reset is performed when scrolled down (e.g. when hot reloading becuase of development or using useControls)
        if (computedStylePortfolioSectionsContainerZIndex < 0) {
            portfolioSectionsContainer.style.zIndex = 1
        }
        if (computedStyleCanvasContainerZIndex < 0) {
            canvasContainer.style.zIndex = 2
        }
        if (computedStyleOverlayContainerZIndex < 0) {
            overlayContainer.style.zIndex = 3
        }

        const cloud = cloudGLTF.scene.children[0].clone()

        const portfolioAssets = portfolioAssetsGLTF.scene.getObjectByName("portfolio-assets")

        // cloning items that are going to be used in the scene
        const movingBallPivot = portfolioAssets.getObjectByName("moving-ball-pivot").clone()

        // TODO: something is not right with how I setup the envelope. The outline wasnt applied to the mesh children (tapa) in the first setup I did so I eneded up setting up the primitives as not children with a separate oultine. But now the problem is when I'm devleoping and sometimes recreating the scene, the mesh children (tapa) keeps around duplicated. But for now.. works
        const envelopeScene = envelopeGLTF.scene
        const envelope = envelopeScene.getObjectByName("envelope").clone()
        const carta = envelope.getObjectByName("carta") // not cloned becuase this is already added with the parent object
        const tapa = envelope.getObjectByName("tapa") // not cloned becuase this is already added with the parent object
        const envelopeAnimationMixer = new THREE.AnimationMixer(envelope)
        const cartaAnimationAction = envelopeAnimationMixer.clipAction(
            THREE.AnimationClip.findByName(envelopeGLTF.animations, "move-carta")
        )
        const envelopeAnimationAction = envelopeAnimationMixer.clipAction(
            THREE.AnimationClip.findByName(envelopeGLTF.animations, "open-envelope")
        )
        const envelopeAnimationDuration = envelopeAnimationAction.getClip().duration
        envelopeAnimationAction.play()
        cartaAnimationAction.play()

        const centerTube = portfolioAssets.getObjectByName("center-tube").clone()
        const rampBall = portfolioAssets.getObjectByName("ball-ramp") // it's cloned when added

        const movingBall = movingBallPivot.getObjectByName("moving-ball").clone()

        const cacharroHolder = portfolioAssets.getObjectByName("cacharro-holder").clone()
        const cacharroTmp = cacharroHolder.getObjectByName("cacharro-tmp")
        cacharroTmp.visible = false

        return [
            debugCenterObject,
            cloud,
            movingBallPivot, movingBall, centerTube, rampBall, cacharroHolder,
            envelope, envelopeAnimationAction, envelopeAnimationDuration,
            cartaAnimationAction, tapa,
            titleNameNormal, titleNameWithDog,
            canvasContainer, computedStyleCanvasContainerZIndex,
            overlayContainer, computedStyleOverlayContainerZIndex,
            portfolioSectionsContainer, computedStylePortfolioSectionsContainerZIndex,
        ]
    }, [
        useNormalMaterial,
        portfolioAssetsGLTF,
        envelopeGLTF
    ])
    // console.log(topCircle)
    // console.log(movingBall)
    // console.log("titleNameNormal: ", titleNameNormal)
    // console.log("titleNameWithDog: ", titleNameWithDog)
    // console.log("envelope: ", envelope)
    // console.log("0-b-tapa: ", tapa)
    // console.log("0-b-tapa.parent: ", tapa.parent)

    const [
        normalMaterial,
        regularMaterialBall, regularMaterialCacharro, regularMaterialCircleRamp, regularMaterialTube,
        paddleMaterial, cloudMaterial,
        ballSmileTexture, ballSurpriseTexture, ballDizzyTexture, ballWinkyTexture,
        debugLineMaterial
    ] = useMemo(() => {
        console.log("calling materials useMemo!")
        const normalMaterial = new THREE.MeshNormalMaterial({
            wireframe: false
        })

        const textureLoader = new THREE.TextureLoader()
        const gradient3Texture = textureLoader.load(preload_assets.textures.gradient3)
        gradient3Texture.magFilter = THREE.NearestFilter
        // const gradient5Texture = textureLoader.load(preload_assets.textures.gradient5)
        // gradient5Texture.magFilter = THREE.NearestFilter
        const ballSmileTexture = textureLoader.load(preload_assets.textures.smile)
        const ballSurpriseTexture = textureLoader.load(preload_assets.textures.surprise)
        const ballDizzyTexture = textureLoader.load(preload_assets.textures.dizzy)
        const ballWinkyTexture = textureLoader.load(preload_assets.textures.winky)

        const regularMaterialBall = new THREE.MeshToonMaterial({
            map: ballSmileTexture,
            gradientMap: gradient3Texture,
        })
        const regularMaterialCacharro = new THREE.MeshToonMaterial({
            color: controlColors.colorCacharro,
            gradientMap: gradient3Texture,
        })
        const regularMaterialCircleRamp = new THREE.MeshToonMaterial({
            color: controlColors.colorTube,
            gradientMap: gradient3Texture,
        })
        const regularMaterialPaddles = new THREE.MeshToonMaterial({
            color: controlColors.colorPaddles,
            gradientMap: gradient3Texture,
        })
        const regularMaterialTube = new THREE.MeshToonMaterial({
            color: controlColors.colorRamp,
            gradientMap: gradient3Texture,
        })

        const debugLineMaterial = new THREE.MeshNormalMaterial({
            wireframe: false
        })

        movingBall.material = useNormalMaterial ? normalMaterial : regularMaterialBall
        rampBall.material = useNormalMaterial ? normalMaterial : regularMaterialCircleRamp
        centerTube.material = useNormalMaterial ? normalMaterial : regularMaterialTube
        const paddleMaterial = useNormalMaterial ? normalMaterial : regularMaterialPaddles
        const cloudMaterial = useNormalMaterial ? normalMaterial : regularMaterialCacharro

        envelope.children.forEach((child) => {
            if (child.name === "carta") {
                child.material = regularMaterialBall
            }
            else {
                child.material = regularMaterialCircleRamp
            }
            // child.material = normalMaterial
            // child.material = regularMaterialTube
            // child.material = regularMaterialBall
        })

        // movingBall.material = normalMaterial
        // rampBall.material = normalMaterial
        // centerTube.material = normalMaterial

        return [
            normalMaterial,
            regularMaterialBall, regularMaterialCacharro, regularMaterialCircleRamp, regularMaterialTube,
            paddleMaterial, cloudMaterial,
            ballSmileTexture, ballSurpriseTexture, ballDizzyTexture, ballWinkyTexture,
            debugLineMaterial,
            controlColors,
        ]
    }, [
        controlColors,
        useNormalMaterial
    ])

    const loadExperiences = () => {
        for (var i = 0; i < experiences.length; i++) {
            experiencesRef.push(useRef())

            const magicNumberOffset = +0.10
            const experienceCU = startExperiencesRotationCameraUnits + experiencesContainerOffsetY + i * oneExperienceCameraUnitsHeight - oneExperienceCameraUnitsHeight / 2.0 + magicNumberOffset
            experiences[i]['scrollPositionPercent'] = experienceCU / totalCameraScrollCameraUnits
        }
    }
    loadExperiences()

    // TODO: esto para intentar mover los Float's según el scroll de la pelota pero no funciona :( no se pueden cambiar los valores de los float en cada frame?? supongo por cómo internamente usa el float q no lee de las propiedas, simplemente sirven para inicializarse así.. que mejor eliminar este array y el intento de modificarlo! Tb eliminar referencias a floatSpeedValue
    const experiencesFloatValues = []
    for (var i = 0; i < experiences.length; i++) {
        experiencesFloatValues.push(10)
    }

    debugCenterObject.visible = showDebugCenterSphere

    useEffect(() => {
        // initial setup, called once
        // console.log(camera.position)

        console.log("recreating! initial useEffect")

        // add camera to a parent group to be able to move the camera
        camera.removeFromParent()
        cameraGroup.add(camera)
        scene.add(cameraGroup)

        camera.add(debugCenterObject)
        camera.add(experienceInfoContainerRef.current)

        for (var i = 0; i < experiencesRef.length; i++) {
            experiencesRef[i].current.rotation.y = experiencesRotationSeparation * i //* 2.0
        }
    }, [])

    const [animationState] = useState({
        animationStatus: 0,
        previousAnimationStatus: -1,
    })

    useFrame((_state, delta) => {
        if (animationState.animationStatus === 0) { return }

        const animationTime = envelopeAnimationAction.getMixer().time
        // console.log("animationStatus: ", animationState.animationStatus)
        // console.log("animationTime: ", animationTime)

        let newAnimationTime = 0
        if (animationState.animationStatus === 1) {
            newAnimationTime = animationTime + delta
        }
        else if (animationState.animationStatus === -1) {
            newAnimationTime = animationTime - delta
        }

        let hasFinished = false
        if (animationState.animationStatus === 1 && newAnimationTime >= envelopeAnimationDuration) {
            hasFinished = true
            envelopeAnimationAction.getMixer().setTime(envelopeAnimationDuration - 0.01)
            cartaAnimationAction.getMixer().setTime(envelopeAnimationDuration - 0.01)
        }
        else if (animationState.animationStatus === -1 && newAnimationTime <= 0.01) {
            hasFinished = true
            envelopeAnimationAction.getMixer().setTime(0.01)
            cartaAnimationAction.getMixer().setTime(0.01)
        }
        else {
            envelopeAnimationAction.getMixer().setTime(newAnimationTime)
            cartaAnimationAction.getMixer().setTime(newAnimationTime)
        }

        if (hasFinished) {
            animationState.previousAnimationStatus = animationState.animationStatus
            animationState.animationStatus = 0
        }
    })

    useFrame((_state, _delta) => {
        // TODO: do ScrollController only places that calculates scrollCameraUnits, now is doing it DebugPanel AND Experience because it has to be inside Canvas
        targetScrollPercent.current = getCurrentScrollPercent()

        if (Math.abs(currentScrollPercent.current - targetScrollPercent.current) < 0.0001) { return }

        if (disableScrollLerpOne.current) {
            currentScrollPercent.current = targetScrollPercent.current
            disableScrollLerpOne.current = false
        }
        else {
            currentScrollPercent.current = THREE.MathUtils.lerp(currentScrollPercent.current, targetScrollPercent.current, cameraLerpFactor)
        }
        const currentScrollCameraUnits = currentScrollPercent.current * totalCameraScrollCameraUnits

        const opacityCameraUnitsThreshold = 0.6
        if (currentScrollCameraUnits >= opacityCameraUnitsThreshold && !titleNameNormal.classList.contains('hidden')) {
            titleNameNormal.classList.add('hidden')
            titleNameWithDog.classList.add('visible')
            // changing title is cool but freezes the scroll :(
            // document.title = bio.presentation.web_title_with_dog
        }
        else if (currentScrollCameraUnits < opacityCameraUnitsThreshold && titleNameWithDog.classList.contains('visible')) {
            titleNameNormal.classList.remove('hidden')
            titleNameWithDog.classList.remove('visible')
            // document.title = bio.presentation.web_title_normal
        }

        // console.log("currentScrollPercent.current: ", currentScrollPercent.current, ". currentScrollCameraUnits: ", currentScrollCameraUnits, ". targetScrollPercent.current: ", targetScrollPercent.current, ". normalNameOpacity: ", normalNameOpacity, ". dogNameOpacity: ", dogNameOpacity)

        // console.log("scrollPercent: " + scrollPercent)
        // const scrollCameraUnitsTwoDecimals = scrollCameraUnits.toFixed(2)
        // console.log("scrollCameraUnitsTwoDecimals " + scrollCameraUnitsTwoDecimals)
        // console.log("Experience. scrollPercent: " + scrollPercent + ", scrollCameraUnitsTwoDecimals: " + scrollCameraUnitsTwoDecimals)

        camera.position.y = -currentScrollCameraUnits

        canvasContainer.style.zIndex = defaultCanvasContainerZIndex
        overlayContainer.style.zIndex = defaultOverlayContainerZIndex
        portfolioSectionsContainer.style.zIndex = defaultPortfolioSectionsContainerZIndex

        const startTopCircleRotation = -Math.PI * 0.5
        const endTopCircleRotation = 0.0
        const movingBallInitialScale = 1.4

        setIgnorePaddlesMouseReaction(false)

        const startFallingBallOffset = initialLetBallFixedInsideRingCameraUnits
        if (currentScrollCameraUnits < initialLetBallFixedInsideRingCameraUnits) {
            const distanceTravelled = currentScrollCameraUnits
            const distanceTotal = (initialLetBallFixedInsideRingCameraUnits)
            const distancePercentage = distanceTravelled / distanceTotal
            // console.log("a. currentDistancePercentage: ", currentDistancePercentage.toFixed(2))

            topCirclePaddlesRef.current.rotation.x = startTopCircleRotation + (endTopCircleRotation - startTopCircleRotation) * distancePercentage
            // console.log("topCirclePaddlesRef.current.rotation.x: ", topCirclePaddlesRef.current.rotation.x)

            movingBall.scale.set(movingBallInitialScale, movingBallInitialScale, movingBallInitialScale)
            movingBall.material.map = ballSmileTexture

            movingBallPivot.position.y = topCircleOffsetY
            movingBallPivot.rotation.set(
                0,
                0,
                0
            )
            movingBall.position.set(0, 0, 0)
            movingBall.rotation.set(
                movingBallInitialRotation.x,
                movingBallInitialRotation.y,
                movingBallInitialRotation.z
            )
        }
        else if (currentScrollCameraUnits >= initialLetBallFixedInsideRingCameraUnits && currentScrollCameraUnits < initialFallingBallCameraUnits) {
            // console.log("b")

            setIgnorePaddlesMouseReaction(true)
            topCirclePaddlesRef.current.rotation.x = endTopCircleRotation

            movingBall.scale.set(movingBallInitialScale, movingBallInitialScale, movingBallInitialScale)
            movingBall.material.map = ballSurpriseTexture

            movingBallPivot.position.y = - (currentScrollCameraUnits - startFallingBallOffset) + topCircleOffsetY
            movingBallPivot.rotation.set(
                0,
                0,
                0
            )
            movingBall.position.set(0, 0, 0)
            movingBall.rotation.set(
                movingBallInitialRotation.x,
                movingBallInitialRotation.y,
                movingBallInitialRotation.z
            )
        }
        else if (currentScrollCameraUnits >= initialFallingBallCameraUnits && currentScrollCameraUnits < finalStopRotatingBallCameraUnits) {
            // console.log("c")

            movingBall.scale.set(1.2, 1.2, 1.2)
            movingBall.material.map = ballDizzyTexture

            const sectionCTraveled = currentScrollCameraUnits - initialFallingBallCameraUnits
            const sectionCTotal = (finalStopRotatingBallCameraUnits - initialFallingBallCameraUnits)
            const sectionCRotationPercentage = sectionCTraveled / sectionCTotal
            const ballRotationSpeedY = 3.0

            movingBallPivot.rotation.set(
                Math.PI / 2.0,
                0,
                0
            )

            // Adjust this to control how much deviation the noise adds
            const noiseAmount = new THREE.Vector3(
                0.7,
                -ballRotationSpeedY,
                0.2
            )
            // Controls how fast the noise changes
            const noiseFrequency = 2.0
            const noise = new THREE.Vector3(
                Math.sin(sectionCRotationPercentage * noiseFrequency * Math.PI * 2.0) * noiseAmount.x,
                Math.sin(sectionCRotationPercentage * noiseFrequency * Math.PI * 2.0) * noiseAmount.z,
                sectionCRotationPercentage * noiseFrequency * Math.PI * 2.0 * noiseAmount.y,
            )
            movingBall.rotation.set(
                Math.PI * 0.5 + noise.x,
                Math.PI * 0.0 + noise.y,
                Math.PI * 0.0 + noise.z,
            )

            const experiencesContainerRefReachedLastRotation = currentScrollCameraUnits > endExperiencesRotationCameraUnits
            let experiencesRotationPercentage = -(currentScrollCameraUnits - startExperiencesRotationCameraUnits) / totalExperiencesRotationCameraUnitsHeight

            // console.log("experiencesContainerRefReachedLastRotation: " + experiencesContainerRefReachedLastRotation)
            if (currentScrollCameraUnits < startExperiencesRotationCameraUnits || experiencesContainerRefReachedLastRotation) {
                // console.log(!experiencesContainerRefReachedLastRotation ? "c.a.1" : "c.a.3")
                experiencesContainerRef.current.rotation.y = experiencesContainerRefReachedLastRotation ? -experiencesContainerTotalRotations : 0.0
                experiencesRotationPercentage = !experiencesContainerRefReachedLastRotation ? 0.0 : -1.0

                // updatePorfolioVisualComponentCacharroHeaderText(
                //     movingBall,
                //     experiencesRef,
                //     debugHighlightExperienceDistanceToCenterBallObject
                // )
            }
            else {
                // console.log("c.a.2")
                // console.log("start scroll: scrollCameraUnits: " + scrollCameraUnits)
                // rotate the experiences container along the scroll and do a complete loop when reaching middleExperiencesHeight, take into account the start and end offsets
                // console.log("experiences experiencesRotationPercentage: " + experiencesRotationPercentage)
                experiencesContainerRef.current.rotation.y = experiencesRotationPercentage * experiencesContainerTotalRotations

                // updatePorfolioVisualComponentsHighlight(
                // updatePorfolioVisualComponentCacharroHeaderText(
                //     movingBall,
                //     experiencesRef,
                //     debugHighlightExperienceDistanceToCenterBallObject
                // )
            }
            // console.log("scrollCameraUnits: " + scrollCameraUnits)

            rampsBallContainerRef.current.rotation.y = experiencesRotationPercentage * rampSpeed

            // -------

            movingBallPivot.position.y = - currentScrollCameraUnits
            movingBall.position.set(0, -1.0, -experiencesBallRampOffsetZ + ballOverRampOffsetZ)

            if (currentScrollCameraUnits <= startExperiencesRotationCameraUnits) {
                // console.log("c.b.1")
                const extraOffsetOverTheRamp = 0.2
                const distanceTravelled = currentScrollCameraUnits - (initialFallingBallCameraUnits - extraOffsetOverTheRamp)
                const distanceTotal = (startExperiencesRotationCameraUnits - (initialFallingBallCameraUnits - extraOffsetOverTheRamp))
                const distancePercentage = distanceTravelled / distanceTotal

                movingBallPivot.rotation.z = distancePercentage * -Math.PI
            }
            else if (!experiencesContainerRefReachedLastRotation) {
                // console.log("c.b.2")
                movingBallPivot.rotation.z = -Math.PI
            }
            else {
                // console.log("c.b.3")
                const distanceTravelled = currentScrollCameraUnits - endExperiencesRotationCameraUnits
                const distanceTotal = (finalStopRotatingBallCameraUnits - endExperiencesRotationCameraUnits)
                const distancePercentage = distanceTravelled / distanceTotal

                movingBallPivot.rotation.z = distancePercentage * -Math.PI
                    // sum Math.PI to make the ball rotate offseted back into the tube
                    + Math.PI
            }
        }
        else {
            // console.log("d")

            overlayContainer.style.zIndex = 3
            canvasContainer.style.zIndex = 1
            portfolioSectionsContainer.style.zIndex = 2
            movingBall.scale.set(1.7, 1.7, 1.7)
            movingBall.material.map = ballWinkyTexture

            movingBallPivot.position.y = - currentScrollCameraUnits + finalBallOffsetY
            movingBallPivot.rotation.set(
                0.0,
                0,
                0
            )
            movingBall.position.set(0, 0, 0)
            movingBall.rotation.set(
                movingBallInitialRotation.x,
                movingBallInitialRotation.y,
                movingBallInitialRotation.z
            )
            experiencesContainerRef.current.rotation.y = -experiencesContainerTotalRotations
        }
    })

    const [cancelZoomIn, setCancelZoomIn] = useState(false)

    const handlePointerMissed = (event) => {
        // TODO: when I miss the click all the PVC are recreated
        // TODO: this toggle is weird I know, not sure how to make an event in a better way. Check this https://react.dev/learn/you-might-not-need-an-effect
        // console.log("handlePointerMissed. cancelZoomIn: ", cancelZoomIn)
        setCancelZoomIn(!cancelZoomIn)
    }

    const [collisionPoint, setCollisionPoint] = useState(new THREE.Vector3())

    return (
        <>
            <group
                onPointerMissed={handlePointerMissed}
            />

            <directionalLight
                position={[0, 5, 3]}
                intensity={3.5}
                color={controlColors.colorCacharro}
            />
            <ambientLight
                intensity={0.5}
                color={controlColors.colorTube}
            />

            <group
                ref={models3dRef}
            >
                <TopCirclePaddles
                    topPaddleContainerRef={topCirclePaddlesRef}
                    countPaddles={totalPaddles}
                    topCircleOffsetY={topCircleOffsetY}
                    paddleMaterial={paddleMaterial}
                    highlightColor={controlColors.colorPaddleHighlight}
                    outlineColor={controlColors.colorText}
                    collisionPoint={collisionPoint}
                    setCollisionPoint={setCollisionPoint}
                    ignorePaddlesMouseReaction={ignorePaddlesMouseReaction}
                />

                <primitive
                    object={movingBallPivot}
                >
                    <primitive
                        object={movingBall}
                    >
                        <Outlines
                            thickness={1.5}
                            color={controlColors.colorText}
                        />
                    </primitive>
                </primitive>

                <group
                >
                    <mesh
                        ref={centerTubeRef}
                        // object={centerTube}
                        geometry={centerTube.geometry}
                        material={centerTube.material}
                        scale-y={centerTubeScaleY}
                        position-y={centerTubeOffsetY}
                    // rotation-x={Math.PI * 0.5}
                    // visible={false}
                    >
                        <Outlines
                            // screenspace={true}
                            // polygonOffset={true}
                            // polygonOffsetFactor={10.0}
                            // angle={100}
                            // angle={Math.PI * 2.0}
                            // angle={Math.PI * 1.0}
                            angle={Math.PI * 0.5}
                            // angle={Math.PI * 0}
                            thickness={1.5}
                            color={controlColors.colorText}
                        // toneMapped={true}
                        />
                    </mesh>
                </group>

                <group
                    ref={rampsBallContainerRef}
                >
                    {Array.from({ length: totalRampBalls }, (_, i) => (
                        <primitive
                            key={"ramp-ball-" + i}
                            object={rampBall.clone()}
                            // scale-x={i % 2 === 0 ? 1 : -1}
                            scale-y={rampBallScaleY}
                            rotation-y={i % 2 === 0 ? 0 : Math.PI}
                            position-y={
                                -startExperiencesRotationCameraUnits - (i + 0) * rampBall.scale.y
                                + experiencesBallRampOffsetZ
                            }
                        // reduce scale if is last ramp ball
                        // position-x={i * 0.3}
                        // visible={false}
                        >
                            <Outlines
                                // screenspace={true}
                                // polygonOffset={true}
                                // polygonOffsetFactor={10.0}
                                // angle={100}
                                // angle={Math.PI * 2.0}
                                // angle={Math.PI * 1.5}
                                // angle={Math.PI * 1.0}
                                // angle={Math.PI * 0.5}
                                // angle={Math.PI * 0}
                                thickness={1.5}
                                color={controlColors.colorText}
                            // toneMapped={true}
                            />
                        </primitive>
                    ))}
                </group>
            </group >

            <group
                ref={experiencesContainerRef}
                position-y={experiencesContainerOffsetY}
            >
                {/* position all experiences pivoting around center */}
                {experiences.map((experience, index) => {
                    return (
                        <PortfolioVisualComponent
                            key={"experience_" + index}
                            index={index}
                            experienceRef={experiencesRef[index]}
                            startExperiencesRotationCameraUnits={startExperiencesRotationCameraUnits}
                            portfolioAssetsAnimations={portfolioAssetsGLTF.animations}
                            cacharroHolder={cacharroHolder.clone()}
                            cacharroPositionY={-index * oneExperienceCameraUnitsHeight}
                            cacharroMaterial={useNormalMaterial ? normalMaterial : regularMaterialCacharro}
                            buttonsColor={controlColors.colorText}
                            floatSpeedValue={experiencesFloatValues[index]}
                            experienceData={experience}
                            cancelZoomIn={cancelZoomIn}
                            movingBall={movingBall}
                        />
                    )
                })}
            </group>

            <group
                ref={experienceInfoContainerRef}
            >
                <Html
                    calculatePosition={((_el, _camera, size) => {
                        return [size.width / 2, size.height * 0.3, 0]
                    })}
                >
                    <div className='experience-info-container-zoom-tooltip'>tap to zoom in</div>
                </Html>

                <Html
                    calculatePosition={((_el, _camera, size) => {
                        return [size.width / 2, size.height * 0.85, 0]
                    })}
                >
                    <div className='experience-info-container'
                        onClick={(event) => {
                            event.stopPropagation()
                        }}
                        onDoubleClick={(event) => {
                            event.stopPropagation()
                        }}
                    >
                        <div className='experience-info-title'></div>
                        <div className='experience-info-description'></div>
                        <div className='experience-info-tags-and-links'>
                            <div className='experience-info-tags'></div>
                            <div className='experience-info-links'></div>
                        </div>
                    </div>
                </Html>
            </group>

            <group
                name={"envelope"}
                position={envelopePosition}
                rotation={[0.0, Math.PI * -0.1, 0]}
                scale={[0.45, 0.45, 0.45]}
            >
                <Float
                    speed={1}
                    rotationIntensity={0.5}
                    floatIntensity={0.5}
                >
                    <primitive
                        object={envelope}
                        onPointerOver={(e) => {
                            // console.log("envelopePointerOver")
                            e.stopPropagation()
                            animationState.animationStatus = 1
                        }}
                        onPointerOut={(e) => {
                            // console.log("envelopePointerOut")
                            e.stopPropagation()
                            animationState.animationStatus = -1
                        }}
                        onPointerDown={(e) => {
                            if (!isMobile()) { return }
                            e.stopPropagation()
                            if (animationState.animationStatus === 0) {
                                animationState.animationStatus = -animationState.previousAnimationStatus
                            }
                        }}
                    >
                        <Outlines
                            thickness={2.0}
                        // angle={Math.PI * 2.0}
                        // color={controlColors.colorText}
                        />
                    </primitive>

                    <primitive
                        object={tapa}
                    // position={[-2.0, 0, 0]}
                    // scale={[1.0, 2.45, 2.45]}
                    >
                        <Outlines
                            thickness={1.0}
                        />
                    </primitive>
                </Float>
            </group>

            <group
                name={"clouds"}
            >
                <Clouds
                    cloud={cloud}
                    cloudMaterial={cloudMaterial}
                    maxCloudDistanceWidth={maxCloudDistanceWidth}
                    totalClouds={totalClouds}
                    cloudSeparationY={cloudSeparationY}
                    startExperiencesCameraUnitsOffset={startExperiencesCameraUnitsOffset}
                />
            </group>

            {
                showDebugLines &&
                <>
                    {Array.from({ length: debugLimitLines.length }, (_, index) => (
                        <mesh
                            key={"debug_line_" + index}
                            material={debugLineMaterial}
                            position={[0.0, -debugLimitLines[index], 0.0]}
                        >
                            <boxGeometry args={[10.0, 0.0001, 0.1]} />
                        </mesh>
                    ))}
                </>
            }

            {
                showDebugNumbers &&
                <>
                    {Array.from({ length: 30 }, (_, index) => (
                        <Html
                            key={"debug_number_" + index}
                            position={[0, -index, 0]}
                            center
                            style={{
                                color: 'white',
                                fontSize: '100px',
                                fontWeight: 'bold',
                                textShadow: '0px 0px 5px black',
                                visibility: showDebugNumbers ? 'visible' : 'hidden'
                            }}
                        >
                            {index}
                        </Html>
                    ))}
                </>
            }
        </>
    )
}